import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowRight, Plus } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';

import type { ProviderDetails } from 'src/api/endpoints/cloudApi.types';
import {
  useCheckoutAllowed,
  useGetAddIntegrationAuthURI,
  useHasSubscription,
} from 'src/api/queries';
import { getLandingPageUrl } from 'src/apps/checkout/app/useCheckoutType';
import { Button } from 'src/components';
import { IntegrationIcon } from 'src/components/IntegrationIcon';

import { type ReactNode, useMemo } from 'react';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { Link2 } from 'src/components/Link2';
import { getActiveOffer } from 'src/utils/checkoutHelpers';
import { URLS } from 'src/utils/urls/urls';

const getProviderItemLocation = (typeName: string) =>
  `${ComponentLocations.INTEGRATIONS.PROVIDER_ITEM} ${typeName}`;

const getProviderDescription = (typeName: string): ReactNode => {
  switch (typeName) {
    case 'MicrosoftCalendar':
      return (
        <Trans>
          Outlook calendar requires an active Microsoft 365 account.
        </Trans>
      );
    case 'Miro':
      return (
        <Trans>
          Send your sketches to a new board in Miro to continue your work.
        </Trans>
      );
    case 'Slack':
      return (
        <Trans>
          Convert notes and sketches with the help of AI, and send them to Slack
          to continue your work.
        </Trans>
      );
    case 'ClioEU':
      return <Trans>Manage your legal practice with Clio.</Trans>;
    default:
      return null;
  }
};

type OfferCheckoutType = 'connectOffer' | 'dmOffer' | 'winbackOffer';
const isOfferCheckoutType = (value: unknown): value is OfferCheckoutType => {
  return (
    value === 'connectOffer' || value === 'dmOffer' || value === 'winbackOffer'
  );
};

export const ProviderItem = ({
  provider,
  disabled = false,
}: {
  provider: ProviderDetails;
  disabled?: boolean;
}) => {
  const { t } = useLingui();
  const description = getProviderDescription(provider.typeName);
  const addIntegrationAuthURI = useGetAddIntegrationAuthURI();
  const hasSubscription = useHasSubscription();

  const checkoutAllowed = useCheckoutAllowed({ select: getActiveOffer });

  const isLoading =
    addIntegrationAuthURI.isPending ||
    hasSubscription.isPending ||
    checkoutAllowed.isPending;

  const isAddButtonDisabled = disabled || isLoading;
  const isProviderAddable = !provider.subscription || hasSubscription.data;
  const buttonType: 'ADD' | 'GET_CONNECT' | 'START_TRIAL' | null =
    useMemo(() => {
      const hasOffer =
        checkoutAllowed.data &&
        isOfferCheckoutType(checkoutAllowed.data.checkoutType);

      if (isProviderAddable) {
        return 'ADD';
      }

      if (hasOffer) {
        return 'START_TRIAL';
      }

      return 'GET_CONNECT';
    }, [provider.subscription, checkoutAllowed.data, isProviderAddable]);

  const handleAdd = (provider: ProviderDetails) => () => {
    addIntegrationAuthURI.mutate(
      { providerPath: provider.subPath },
      {
        onSuccess: (response) => {
          /* TODO: Fix redirect_uri

        The url we receive from the API does not point to the active
        environment. We need to fix this in the API or whitelist
        the relevant domains in the Auth provider so that we can
        use the redirect_uri param from the client
        */
          location.href = response;
        },
      }
    );
  };

  return (
    <div className="flex flex-col gap-16">
      <div className="flex flex-col gap-24 lm:flex-row">
        <span className="flex min-w-0 flex-col flex-nowrap gap-24 lm:flex-1">
          <span className="flex flex-wrap items-center gap-16">
            <IntegrationIcon
              alt={provider.displayName}
              className="w-32"
              iconId={provider.iconId}
            />
            <Typography
              variant="heading-subtle-lg"
              className="md:heading-subtle-2xl"
              as="h2"
            >
              {provider.displayName}
            </Typography>
          </span>

          {description && (
            <Typography
              variant="body-md"
              textWrap="pretty"
              className="max-w-[65ch]"
            >
              {description}
            </Typography>
          )}
        </span>
        {buttonType === 'ADD' && (
          <Button
            variant="secondary"
            onClick={handleAdd(provider)}
            loading={isLoading}
            disabled={isAddButtonDisabled}
            data-cy={`add-provider-button`}
            size="small"
            analytics={{
              action: 'add integration',
              location: getProviderItemLocation(provider.typeName),
              text: t`Add`,
            }}
            className="w-full shrink-0 items-center self-start whitespace-nowrap ls:w-fit"
          >
            <span>
              <Trans>Add</Trans>
            </span>
            <Plus />
          </Button>
        )}
        {buttonType === 'GET_CONNECT' && (
          <Link2
            as="button"
            variant="secondary"
            to={URLS.STORE_CONNECT_CHECKOUT}
            data-cy={`provider-get-connect-button`}
            size="small"
            componentLocation={getProviderItemLocation(provider.typeName)}
            className="w-full shrink-0 items-center self-start text-14! whitespace-nowrap ls:w-fit"
          >
            <span>
              <Trans>Get Connect</Trans>
            </span>
            <ArrowRight />
          </Link2>
        )}
        {buttonType === 'START_TRIAL' && (
          <Link2
            as="button"
            variant="secondary"
            to={getLandingPageUrl(checkoutAllowed.data?.checkoutType)}
            data-cy={`provider-activate-trial-button`}
            className="w-full shrink-0 self-start text-14! whitespace-nowrap ls:w-fit"
            size="small"
            componentLocation={getProviderItemLocation(provider.typeName)}
          >
            <span>
              <Trans>Start free trial</Trans>
            </span>
            <ArrowRight />
          </Link2>
        )}
      </div>
    </div>
  );
};
